import { Analytics } from '@easy-expense/analytics-client';
import { ReportWithData } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getTripDeduction } from '@easy-expense/utils-shared';
import { getMRT_RowSelectionHandler, MRT_RowSelectionState } from 'material-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../Button.components';
import { CloseButton } from '../Shared/CloseButton.component';
import { TripTable } from '../Trip/TripList';

export const ReportAddTrips: React.FC<
  React.PropsWithChildren<{
    reportKey?: string;
    onClose: () => void;
    report: ReportWithData;
    setReportDirty?: (isDirty: boolean) => void;
  }>
> = ({ onClose, report, setReportDirty }) => {
  const navigate = useNavigate();
  // `report.trips` is mutated in place as rows are selected, so what the report
  // arrived with has to be held onto if the additions are to be counted.
  const initialTrips = React.useRef(report?.trips ?? []);
  const tripObjects = Data.trips.use();
  const tripClasses = Data.tripClasses.use();
  const workspaceRateDetails = Data.rateDetails.use();
  const countryRateDetails = Data.countryRateDetails.useCountryRate();

  if (!report) {
    navigate('/404');
  }

  async function onDone() {
    report.tripTotal = report.trips.reduce((acc, trip) => {
      const tripObj = tripObjects.find((e) => e.key === trip);
      const tripClass = tripClasses.find((c) => c.key === tripObj?.tripClass);
      if (tripObj && tripClass) {
        return acc + getTripDeduction(tripObj, tripClass, workspaceRateDetails, countryRateDetails);
      }
      return acc;
    }, 0);

    const added = report.trips.filter((trip) => !initialTrips.current.includes(trip));
    if (added.length) {
      Analytics.track('trip_added_to_report', { n: added.length, source: 'report' });
    }

    onClose();
  }

  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>(
    report.trips.reduce((acc: any, trip) => {
      acc[trip] = true;
      return acc;
    }, {}),
  );

  React.useEffect(() => {
    if (setReportDirty) {
      setReportDirty(true);
    }
    const newSelected = Object.keys(rowSelection);
    report.trips = newSelected;
  }, [rowSelection]);

  return (
    <Layout.Row px={24} style={{ height: '100%', width: '100%' }}>
      <Spacer.Flex />
      <Layout.Column style={{ height: '100%', minWidth: 1100 }}>
        <Layout.Row py align>
          <OpenSans.Primary weight="bold-700" size={32}>
            {getTranslation('Select and add to')} {report?.name}
          </OpenSans.Primary>
          <Spacer.Horizontal size={32} />
          <Layout.Column>
            <Button.Primary radius={100} onClick={onDone}>
              <Icon name="checkmark-done" size={20} color={theme.colors.white} />
              <Spacer.Horizontal size={8} />
              {getTranslation('Done')}
            </Button.Primary>
          </Layout.Column>
        </Layout.Row>

        <TripTable
          onRowClick={() => {}}
          additionalOptions={{
            enableRowSelection: true,
            onRowSelectionChange: setRowSelection,
            state: { rowSelection },
            muiTableBodyRowProps: ({ row, staticRowIndex, table }) => ({
              onClick: (event) => getMRT_RowSelectionHandler({ row, staticRowIndex, table })(event),
              sx: { cursor: 'pointer' },
            }),
          }}
        />
      </Layout.Column>
      <Spacer.Flex />
      <CloseButton onClose={onDone} />
    </Layout.Row>
  );
};
